<template>
  <div class="dashboard">
    <MobileHeader @toggle-sidebar="toggleSidebar" />
    
    <Sidebar 
      :isOpen="sidebarOpen" 
      :isMobile="isMobile" 
      @close="closeSidebar"
    />
    
    <main class="main-content" :class="{ 'main-content-mobile': isMobile }">
      <header class="header">
        <h1>Управление на църкви</h1>
        <button @click="showCreateModal = true" class="btn btn-primary">
          <i class="fas fa-plus"></i>
          Добави църква
        </button>
      </header>
      
      <div class="content">
        <div class="card">
          <div class="card-header">
            <h3>Всички църкви</h3>
            <p class="subtitle">Управление на църквите и тяхните настройки</p>
          </div>
          
          <div v-if="loading" class="loading">
            <i class="fas fa-spinner fa-spin"></i>
            Зареждане на църкви...
          </div>
          
          <div v-else-if="churchesList.length === 0" class="no-data">
            <i class="fas fa-church"></i>
            <p>Няма намерени църкви</p>
            <button @click="showCreateModal = true" class="btn btn-primary">
              Създай първа църква
            </button>
          </div>
          
          <div v-else class="churches-grid">
            <div
              v-for="church in churchesList"
              :key="church.id"
              class="church-card"
            >
              <div class="church-thumbnail">
                <img 
                  v-if="church.thumbnail" 
                  :src="church.thumbnail" 
                  :alt="church.name.bg"
                  class="thumbnail-image"
                />
                <div v-else class="thumbnail-placeholder">
                  <i class="fas fa-church"></i>
                </div>
              </div>
              
              <div class="church-info">
                <h4 class="church-name">{{ church.name.bg }}</h4>
                <!-- Temporarily removed English name display
                <p class="church-name-en">{{ church.name.en }}</p>
                -->
                
                <div class="church-details">
                  <div class="detail-item" v-if="church.eventsEndpoint">
                    <i class="fas fa-calendar"></i>
                    <span>Събития: {{ church.eventsEndpoint }}</span>
                  </div>
                  
                  <div class="detail-item" v-if="church.youtubeChannelId">
                    <i class="fab fa-youtube"></i>
                    <span>YouTube: {{ church.youtubeChannelId }}</span>
                  </div>
                  
                  <div class="detail-item" v-if="church.donate && church.donate.bg">
                    <i class="fas fa-heart"></i>
                    <span>Дарения: BG</span>
                  </div>
                </div>
              </div>
              
              <div class="church-actions">
                <button
                  @click="editChurch(church)"
                  class="btn btn-sm btn-primary"
                  title="Редактирай църква"
                >
                  <i class="fas fa-edit"></i>
                </button>
                <button
                  @click="deleteChurch(church)"
                  class="btn btn-sm btn-danger"
                  title="Изтрий църква"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
    
    <!-- Create/Edit Church Modal -->
    <div v-if="showCreateModal || editingChurch" class="modal-overlay" @click="closeModal">
      <div class="modal modal-large" @click.stop>
        <div class="modal-header">
          <h3>{{ editingChurch ? 'Редактирай църква' : 'Създай нова църква' }}</h3>
          <button @click="closeModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <form @submit.prevent="saveChurch">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Име на български <span class="required-asterisk">*</span></label>
                <input
                  type="text"
                  v-model="churchForm.name.bg"
                  class="form-control"
                  placeholder="Въведете име на български"
                  required
                />
              </div>
              
              <!-- Temporarily removed English name field
              <div class="form-group">
                <label class="form-label">Име на английски <span class="required-asterisk">*</span></label>
                <input
                  type="text"
                  v-model="churchForm.name.en"
                  class="form-control"
                  placeholder="Enter name in English"
                  required
                />
              </div>
              -->
            </div>
            
            <div class="form-group">
              <label class="form-label">Снимка</label>
              <input
                type="text"
                v-model="churchForm.thumbnail"
                class="form-control"
                placeholder="URL на снимката или път до локален файл"
              />
              <small class="form-text">Адрес на изображението</small>
            </div>
            
            <div class="form-group">
              <label class="form-label">Events Endpoint <span class="required-asterisk">*</span></label>
              <input
                type="url"
                v-model="churchForm.eventsEndpoint"
                class="form-control"
                placeholder="https://api.example.com/events"
                required
              />
              <small class="form-text">API endpoint за събития на църквата</small>
            </div>
            
            <div class="form-group">
              <label class="form-label">YouTube Channel ID <span class="required-asterisk">*</span></label>
              <input
                type="text"
                v-model="churchForm.youtubeChannelId"
                class="form-control"
                placeholder="UCxxxxxxxxxxxxxxxxx"
                required
              />
              <small class="form-text">ID на YouTube канала на църквата</small>
            </div>

            <div class="form-group">
              <label class="form-label">Donate Content (BG)</label>
              <div class="rich-editor-container">
                <QuillEditor
                  v-model:content="churchForm.donate.bg"
                  content-type="html"
                  :options="donateEditorOptions"
                  placeholder="Въведете информация за дарения на български (HTML съдържание)"
                />
              </div>
              <small class="form-text">Богат текст с HTML форматиране за страницата за дарения на български</small>
            </div>

            <!-- Temporarily removed English donate content
            <div class="form-group">
              <label class="form-label">Donate Content (EN)</label>
              <div class="rich-editor-container">
                <QuillEditor
                  v-model:content="churchForm.donate.en"
                  content-type="html"
                  :options="donateEditorOptions"
                  placeholder="Enter donation information in English (HTML content)"
                />
              </div>
              <small class="form-text">Rich text with HTML formatting for the donation page in English</small>
            </div>
            -->
            
            <div class="modal-actions">
              <button type="button" @click="closeModal" class="btn btn-secondary">
                Отказ
              </button>
              <button type="submit" class="btn btn-primary" :disabled="saving">
                <i class="fas fa-save"></i>
                {{ saving ? 'Запазване...' : 'Запази' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <div v-if="churchToDelete" class="modal-overlay" @click="cancelDelete">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>Потвърждение за изтриване</h3>
          <button @click="cancelDelete" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <p>Сигурни ли сте, че искате да изтриете църквата "{{ churchToDelete.name.bg }}"?</p>
          <p class="warning-text">
            <i class="fas fa-warning"></i>
            Това действие не може да бъде отменено. Всички потребители, свързани с тази църква, ще загубят връзката си.
          </p>
          
          <div class="modal-actions">
            <button @click="cancelDelete" class="btn btn-secondary">
              Отказ
            </button>
            <button @click="confirmDelete" class="btn btn-danger" :disabled="deleting">
              <i class="fas fa-trash"></i>
              {{ deleting ? 'Изтриване...' : 'Изтрий' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { database } from '../firebase'
import { ref as dbRef, onValue, push, update, remove } from 'firebase/database'
import Sidebar from './Sidebar.vue'
import MobileHeader from './MobileHeader.vue'

export default {
  name: 'ChurchesPage',
  components: {
    Sidebar,
    MobileHeader
  },
  data() {
    return {
      loading: true,
      churches: {},
      users: {},
      showCreateModal: false,
      editingChurch: null,
      churchToDelete: null,
      saving: false,
      deleting: false,
      sidebarOpen: false,
      isMobile: false,
      churchForm: {
        name: {
          bg: '',
          en: ''
        },
        thumbnail: '',
        eventsEndpoint: '',
        youtubeChannelId: '',
        donate: {
          bg: '',
          en: ''
        }
      },
      donateEditorOptions: {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['link'],
            ['clean']
          ]
        }
      }
    }
  },
  computed: {
    churchesList() {
      return Object.entries(this.churches).map(([id, church]) => ({
        id,
        ...church
      })).sort((a, b) => a.name.bg.localeCompare(b.name.bg))
    },
    usersList() {
      return Object.entries(this.users).map(([id, user]) => ({
        id,
        ...user
      }))
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen
    },
    closeSidebar() {
      this.sidebarOpen = false
    },
    editChurch(church) {
      this.editingChurch = church
      this.churchForm = {
        name: {
          bg: church.name.bg || '',
          en: church.name.en || ''
        },
        thumbnail: church.thumbnail || '',
        eventsEndpoint: church.eventsEndpoint || '',
        youtubeChannelId: church.youtubeChannelId || '',
        donate: {
          bg: church.donate?.bg || '',
          en: church.donate?.en || ''
        }
      }
    },
    
    closeModal() {
      this.showCreateModal = false
      this.editingChurch = null
      this.resetForm()
    },
    
    resetForm() {
      this.churchForm = {
        name: {
          bg: '',
          en: ''
        },
        thumbnail: '',
        eventsEndpoint: '',
        youtubeChannelId: '',
        donate: {
          bg: '',
          en: ''
        }
      }
    },
    
    async saveChurch() {
      if (!this.churchForm.name.bg.trim() || 
          !this.churchForm.eventsEndpoint.trim() || !this.churchForm.youtubeChannelId.trim()) {
        alert('Моля, въведете всички задължителни полета.')
        return
      }
      
      this.saving = true
      
      try {
        const churchData = {
          name: {
            bg: this.churchForm.name.bg.trim(),
            en: '' // Temporarily disabled for BG-only version
          },
          thumbnail: this.churchForm.thumbnail.trim() || null,
          eventsEndpoint: this.churchForm.eventsEndpoint.trim() || null,
          youtubeChannelId: this.churchForm.youtubeChannelId.trim() || null,
          donate: {
            bg: this.churchForm.donate.bg || null,
            en: '' // Temporarily disabled for BG-only version
          },
          createdAt: this.editingChurch ? this.editingChurch.createdAt : new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        
        if (this.editingChurch) {
          // Update existing church
          await update(dbRef(database, `churches/${this.editingChurch.id}`), churchData)
        } else {
          // Create new church
          await push(dbRef(database, 'churches'), churchData)
        }
        
        this.closeModal()
      } catch (error) {
        console.error('Error saving church:', error)
        alert('Възникна грешка при запазването на църквата. Моля, опитайте отново.')
      } finally {
        this.saving = false
      }
    },
    
    deleteChurch(church) {
      this.churchToDelete = church
    },
    
    cancelDelete() {
      this.churchToDelete = null
    },
    
    async confirmDelete() {
      if (!this.churchToDelete) return
      
      this.deleting = true
      
      try {
        await remove(dbRef(database, `churches/${this.churchToDelete.id}`))
        this.churchToDelete = null
      } catch (error) {
        console.error('Error deleting church:', error)
        alert('Възникна грешка при изтриването на църквата. Моля, опитайте отново.')
      } finally {
        this.deleting = false
      }
    },
    
    loadChurches() {
      const churchesRef = dbRef(database, 'churches')
      onValue(churchesRef, (snapshot) => {
        this.churches = snapshot.val() || {}
        this.loading = false
      })
    },
    
    loadUsers() {
      const usersRef = dbRef(database, 'users')
      onValue(usersRef, (snapshot) => {
        this.users = snapshot.val() || {}
      })
    }
  },
  mounted() {
    this.loadChurches()
    this.loadUsers()
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
  }
}
</script>

<style scoped>
.dashboard {
  display: flex;
  min-height: 100vh;
  flex-direction: column;
}

.main-content {
  flex: 1;
  margin-left: 260px;
  background: #F8F8F8;
}

.main-content-mobile {
  margin-left: 0;
}

.header {
  background: white;
  padding: 24px 32px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header h1 {
  color: #333333;
  font-size: 28px;
  font-size: min(max(20px, 4vw), 28px);
  font-weight: 700;
}

.content {
  padding: 32px 16px;
}

.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 24px;
}

.card-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.card-header h3 {
  color: #333333;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
}

.subtitle {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
}

.loading {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.loading i {
  font-size: 24px;
  margin-bottom: 12px;
  color: #1D687B;
}

.no-data {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.no-data i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.3;
}

.no-data p {
  margin-bottom: 20px;
}

.churches-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
}

@media (max-width: 768px) {
  .churches-grid {
    grid-template-columns: 1fr !important;
    gap: 16px !important;
  }
  
  .church-card {
    padding: 16px;
    width: 100%;
    box-sizing: border-box;
  }
  
  .content {
    padding: 16px 12px;
  }
}

.church-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  transition: all 0.3s ease;
  position: relative;
}

.church-card:hover {
  border-color: #1D687B;
  box-shadow: 0 4px 6px rgba(29, 104, 123, 0.1);
}

.church-thumbnail {
  width: 100%;
  height: 160px;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 16px;
  background: #F8F8F8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  color: #6b7280;
  font-size: 48px;
}

.church-info {
  margin-bottom: 16px;
}

.church-name {
  color: #333333;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
}

.church-name-en {
  color: #6b7280;
  font-size: 14px;
  font-style: italic;
  margin-bottom: 12px;
}

.church-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
}

.detail-item span {
  flex: 1;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}

.detail-item i {
  width: 16px;
  min-width: 16px;
  color: #1D687B;
  flex-shrink: 0;
}

.church-actions {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 12px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #1D687B;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #155660;
}

.btn-secondary {
  background: #79daf2;
  color: #1D687B;
}

.btn-secondary:hover:not(:disabled) {
  background: #66c7e0;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

.btn-sm {
  padding: 10px 14px;
  font-size: 14px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  margin: 20px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-large {
  max-width: 800px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #F8F8F8;
}

.modal-header h3 {
  color: #333333;
  font-size: 20px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 10px;
  border-radius: 8px;
  transition: color 0.3s ease;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.close-btn:hover {
  color: #374151;
  background-color: #f3f4f6;
}

.modal-body {
  padding: 24px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #333333;
  font-size: 14px;
}

/* Make required asterisks red */
.required-asterisk {
  color: #ef4444;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #1D687B;
  box-shadow: 0 0 0 3px rgba(29, 104, 123, 0.1);
}

/* Rich text editor styles */
.rich-editor-container {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
  transition: border-color 0.3s ease;
}

.rich-editor-container:focus-within {
  border-color: #1D687B;
  box-shadow: 0 0 0 3px rgba(29, 104, 123, 0.1);
}

.rich-editor-container .ql-editor {
  min-height: 200px;
  font-size: 14px;
  line-height: 1.5;
}

.rich-editor-container .ql-toolbar {
  border-bottom: 1px solid #e5e7eb;
  background: #f8f9fa;
}

.rich-editor-container .ql-container {
  border: none;
  font-family: inherit;
}

.form-text {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.warning-text {
  color: #ef4444;
  font-size: 14px;
  background: #fef2f2;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  margin-bottom: 20px;
}

.warning-text i {
  margin-right: 8px;
}

@media (max-width: 768px) {
  .card {
    padding: 0;
    box-shadow: none;
    border-radius: 0;
    background-color: transparent;
  }

  .card-header {
    display: none;
  }

  .main-content {
    margin-left: 0;
  }
  
  .header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    padding: 16px 20px;
  }
  
  .churches-grid {
    grid-template-columns: 1fr !important;
    gap: 16px !important;
  }
  
  .church-card {
    padding: 16px;
    width: 100%;
    box-sizing: border-box;
    background-color: #FFF;
  }
  
  .content {
    padding: 16px 12px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .modal-actions {
    flex-direction: column;
  }
}
</style>
